import React, { useState, useEffect } from 'react';
import { ChevronRight, Check, X, AlertCircle, UserPlus } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import CustomerFormModal from '../shared/CustomerFormModal';
import SCREEN_IDS from '../../utils/screenIds';
import { todayCentral } from '../../shared/utils/dateUtils';

const SchemeEnrollment = () => {
  const [step, setStep] = useState(1); // 1: Select Customer, 2: Select Scheme, 3: Set Dates, 4: Review
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Step 1: Customer Selection
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // Step 2: Scheme Selection
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [enrollmentType, setEnrollmentType] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [schemesLoading, setSchemesLoading] = useState(false);

  // Step 3: Date Selection
  const [enrollmentDate, setEnrollmentDate] = useState(todayCentral());
  const [startDate, setStartDate] = useState(todayCentral());
  const [durationMonths, setDurationMonths] = useState(12);

  // Review & Submit
  const [submitting, setSubmitting] = useState(false);

  // Fetch schemes on mount
  useEffect(() => {
    fetchSchemes();
  }, []);

  const fetchSchemes = async () => {
    try {
      setSchemesLoading(true);
      const response = await fetch('/api/schemes');
      const result = await response.json();
      
      if (result.success) {
        setSchemes(result.data);
      } else {
        setError('Failed to fetch schemes');
      }
    } catch (err) {
      setError('Error fetching schemes: ' + err.message);
    } finally {
      setSchemesLoading(false);
    }
  };

  const searchCustomers = async (term) => {
    if (term.length < 2) {
      setCustomers([]);
      return;
    }

    try {
      setCustomersLoading(true);
      const response = await fetch(`/api/customers/search?term=${encodeURIComponent(term)}&limit=10`);
      const result = await response.json();
      
      if (result.success) {
        setCustomers(result.data);
      }
    } catch (err) {
      setError('Error searching customers: ' + err.message);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleCustomerSearch = (value) => {
    setSearchTerm(value);
    searchCustomers(value);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setCustomers([]);
    setStep(2);
  };

  const handleCustomerCreated = (newCustomer) => {
    setSelectedCustomer(newCustomer);
    setShowCustomerModal(false);
    setStep(2);
    setSuccess(`Customer ${newCustomer.customer_code} created successfully!`);
  };

  const handleSelectScheme = (scheme) => {
    setSelectedScheme(scheme);
    setEnrollmentType(scheme.scheme_type === 'fixed' ? 'fixed_group' : 'custom_individual');
    
    if (scheme.scheme_type === 'custom') {
      setDurationMonths(scheme.duration_months);
      setCustomAmount(scheme.monthly_amount.toString());
    } else {
      setCustomAmount(scheme.monthly_amount.toString());
      setDurationMonths(scheme.duration_months);
    }
    
    setStep(3);
  };

  const calculateMaturityDate = () => {
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + durationMonths);
    return start.toISOString().split('T')[0];
  };

  const calculateTotalAmount = () => {
    const monthly = parseFloat(customAmount) || 0;
    const duration = parseInt(durationMonths) || 1;
    return (monthly * duration).toFixed(2);
  };

  const handleEnroll = async () => {
    if (!selectedCustomer || !selectedScheme || !customAmount || !durationMonths) {
      setError('Please complete all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const enrollmentData = {
        customer_id: selectedCustomer.id,
        scheme_id: selectedScheme.id,
        enrollment_type: enrollmentType,
        monthly_amount: parseFloat(customAmount),
        duration_months: parseInt(durationMonths),
        enrollment_date: enrollmentDate,
        start_date: startDate
      };

      console.log('Submitting enrollment:', enrollmentData);

      const response = await fetch('/api/schemes/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrollmentData)
      });

      const result = await response.json();

      console.log('Enrollment response:', result);

      if (result.success) {
        setSuccess(`${selectedCustomer.name} enrolled successfully!`);
        // Reset form
        setStep(1);
        setSelectedCustomer(null);
        setSelectedScheme(null);
        setSearchTerm('');
        setEnrollmentDate(todayCentral());
        setStartDate(todayCentral());
        setCustomAmount('');
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.error || 'Failed to enroll customer');
      }
    } catch (err) {
      console.error('Enrollment error:', err);
      setError('Error enrolling customer: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.ENROLLMENT} pageName="Enroll Customer" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Enroll Customer in Scheme</h1>
        <p className="text-gray-500 mt-1">Create a new gold monthly installment enrollment</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full font-bold transition ${
                  step >= stepNum
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {stepNum}
              </div>
              {stepNum < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 transition ${
                    step > stepNum ? 'bg-amber-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span>Select Customer</span>
          <span>Select Scheme</span>
          <span>Set Dates</span>
          <span>Review</span>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <Check size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Step 1: Select Customer */}
      {step === 1 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Step 1: Select Customer</h2>
            <button
              onClick={() => setShowCustomerModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <UserPlus size={20} />
              Create New Customer
            </button>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for customer
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleCustomerSearch(e.target.value)}
              placeholder="Search by name, phone, or email..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {customersLoading && (
            <p className="text-gray-600 py-4">Searching...</p>
          )}

          {customers.length > 0 && (
            <div className="space-y-2 mb-6">
              {customers.map(customer => (
                <button
                  key={customer.id}
                  onClick={() => handleSelectCustomer(customer)}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition flex items-center justify-between group"
                >
                  <div>
                    <p className="font-semibold text-gray-800">{customer.name}</p>
                    <p className="text-sm text-gray-500">{customer.phone}</p>
                  </div>
                  <ChevronRight className="text-gray-400 group-hover:text-amber-500" />
                </button>
              ))}
            </div>
          )}

          {searchTerm && customers.length === 0 && !customersLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-gray-600 mb-3">No customers found matching your search.</p>
              <button
                onClick={() => setShowCustomerModal(true)}
                className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
              >
                <UserPlus size={18} />
                Create a new customer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Scheme */}
      {step === 2 && selectedCustomer && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
          <h2 className="text-2xl font-bold mb-2 text-gray-800">Step 2: Select Scheme</h2>
          <p className="text-gray-600 mb-4">Customer: <span className="font-semibold">{selectedCustomer.name}</span></p>

          {schemesLoading ? (
            <p className="text-gray-600 py-4">Loading schemes...</p>
          ) : schemes.length === 0 ? (
            <p className="text-gray-500 py-4">No schemes available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {schemes.map(scheme => (
                <button
                  key={scheme.id}
                  onClick={() => handleSelectScheme(scheme)}
                  className="text-left p-4 border border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition"
                >
                  <h3 className="font-bold text-gray-800">{scheme.scheme_name}</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {scheme.scheme_type === 'fixed' ? 'Fixed Group' : 'Custom Individual'}
                  </p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly:</span>
                      <span className="font-semibold text-amber-600">
                        {scheme.currency === 'USD' ? '$' : '₹'}{parseFloat(scheme.monthly_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-semibold">{scheme.duration_months} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-semibold text-amber-600">
                        {scheme.currency === 'USD' ? '$' : '₹'}{parseFloat(scheme.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => setStep(1)}
            className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
          >
            ← Back to Customer Selection
          </button>
        </div>
      )}

      {/* Step 3: Set Dates */}
      {step === 3 && selectedCustomer && selectedScheme && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Step 3: Set Dates</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Customer:</strong> {selectedCustomer.name}
            </p>
            <p className="text-sm text-blue-800">
              <strong>Scheme:</strong> {selectedScheme.scheme_name}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enrollment Date *
              </label>
              <input
                type="date"
                value={enrollmentDate}
                onChange={(e) => setEnrollmentDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {selectedScheme.scheme_type === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Amount ({selectedScheme.currency}) *
                  </label>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (Months) *
                  </label>
                  <input
                    type="number"
                    value={durationMonths}
                    onChange={(e) => setDurationMonths(e.target.value)}
                    min="1"
                    max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>

          {/* Summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Enrollment Summary:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-600">Monthly</p>
                <p className="font-bold text-amber-600">
                  {selectedScheme.currency === 'USD' ? '$' : '₹'}{parseFloat(customAmount || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Duration</p>
                <p className="font-bold">{durationMonths} months</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Total Amount</p>
                <p className="font-bold text-amber-600">
                  {selectedScheme.currency === 'USD' ? '$' : '₹'}{calculateTotalAmount()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Maturity Date</p>
                <p className="font-bold">{new Date(calculateMaturityDate()).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setStep(4)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2"
            >
              <ChevronRight size={20} /> Review & Enroll
            </button>
            <button
              onClick={() => setStep(2)}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && selectedCustomer && selectedScheme && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Step 4: Review & Confirm</h2>

          <div className="space-y-4 mb-6">
            {/* Customer Info */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Customer</p>
              <p className="font-bold text-gray-800">{selectedCustomer.name}</p>
              <p className="text-sm text-gray-600">{selectedCustomer.phone}</p>
            </div>

            {/* Scheme Info */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Scheme</p>
              <p className="font-bold text-gray-800">{selectedScheme.scheme_name}</p>
              <p className="text-sm text-gray-600">
                {enrollmentType === 'fixed_group' ? 'Fixed Group' : 'Custom Individual'}
              </p>
            </div>

            {/* Enrollment Details */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-3">Enrollment Details</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Enrollment Date</p>
                  <p className="font-semibold">{new Date(enrollmentDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Start Date</p>
                  <p className="font-semibold">{new Date(startDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Monthly Amount</p>
                  <p className="font-semibold text-amber-600">
                    {selectedScheme.currency === 'USD' ? '$' : '₹'}{parseFloat(customAmount).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Duration</p>
                  <p className="font-semibold">{durationMonths} months</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Amount</p>
                  <p className="font-semibold text-amber-600">
                    {selectedScheme.currency === 'USD' ? '$' : '₹'}{calculateTotalAmount()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Maturity Date</p>
                  <p className="font-semibold">{new Date(calculateMaturityDate()).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800">
              ✓ Enrollment will automatically generate <strong>{durationMonths} payment records</strong> with correct due dates.
            </p>
            <p className="text-sm text-green-800 mt-2">
              ✓ Company will automatically pay month <strong>{selectedScheme.company_bonus_month}</strong>.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleEnroll}
              disabled={submitting}
              className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2"
            >
              <Check size={20} /> {submitting ? 'Enrolling...' : 'Confirm & Enroll'}
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={submitting}
              className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Customer Creation Modal */}
      <CustomerFormModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerCreated={handleCustomerCreated}
      />
    </div>
  );
};

export default SchemeEnrollment;
